import { useMemo, useState } from 'react'

import { useFacilitySearchOptions } from '@/hooks/useFacilitySearchOptions'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import {
  MAX_FILENAME_LENGTH,
  MAX_PDF_SIZE,
  UPLOAD_ERROR_COMPONENTS,
  reportUploadError,
  useUpload,
} from '@/hooks/useUpload'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Modal } from '@hivewatch/dark'

import {
  ControlledMultiSelectInput,
  ControlledSelect,
  ControlledTextInput,
  FormFileUploadControl,
} from '@/components/ui'
import { useCreateStandardOperatingProcedureMutation } from '@/graphql/generated/hooks'
import { SopType, UploadedFileType } from '@/graphql/generated/schemas'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import {
  FacilityOptionWithAddressI,
  OptionI,
} from '@/utils/forms/optionBuilders'
import { tVar } from '@/utils/i18n'

import { useFacilitySelectAll } from '../hooks'
import { SOP_ROLES } from '../utils/utils'
import { FacilitySelectHeader } from './FacilitySelectHeader'

interface DarkCreateSOPModalIProps {
  isOpen: boolean
  onClose: () => void
}

interface FormInputsIProps {
  title: string
  facilities?: OptionI[]
  role: OptionI<SopType>
  file: FileList
}

const PDF_MIME_TYPE = 'application/pdf'

/**
 * Rebrand variant of {@link CreateSOPModal} built on `@hivewatch/dark`. Upload,
 * mutation, refetch and all four `trackEvent` calls are unchanged. The PDF
 * field stays on `FormFileUploadControl` — the design system has no file-upload
 * component — keyed to the theme through its `colorScheme` prop.
 */
export const DarkCreateSOPModal = ({
  isOpen,
  onClose,
}: DarkCreateSOPModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const [isLoading, setIsLoading] = useState(false)
  const { shouldEnableCreateSOP } = usePermissions()
  const { showSuccess, showError } = useToasts()

  const [upload] = useUpload()
  const [createStandardOperatingProcedure] =
    useCreateStandardOperatingProcedureMutation()
  const { facilityOptions, isFacilitiesLoading, setFacilityName } =
    useFacilitySearchOptions()
  const {
    handleSubmit,
    register,
    control,
    errors,
    formState,
    trigger,
    watch,
    setValue,
  } = useForm<FormInputsIProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { isValid } = formState

  // MultiSelectInput's subtitle/caption are plain presentation strings, so
  // the "matched by address" cue is translated here rather than baked into
  // getIncidentFacilityOptionsWithAddress (which stays i18n-agnostic).
  const facilityOptionsWithCue = useMemo(
    () =>
      (facilityOptions as FacilityOptionWithAddressI[]).map((option) => ({
        ...option,
        caption:
          option.matchedField === 'address'
            ? t('common:facility_matched_by_address')
            : undefined,
      })),
    [facilityOptions, t]
  )

  const { areAllFacilitiesSelected, handleSelectAllFacilities } =
    useFacilitySelectAll({
      watch,
      setValue,
      facilityOptions,
    })

  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    trackEvent(`${BUTTON_PRESS} Upload SOP`, {
      sop_audience: values.role.value,
    })
    setIsLoading(true)

    const file = values.file.item(0)
    const sopTitle = values.title
    const facilityIds = values.facilities?.map((f) => f.value) || []

    try {
      const fileId = await upload({
        file,
        fileType: UploadedFileType.StandardOperatingProcedure,
        validationOptions: {
          maxFileSizeInBytes: MAX_PDF_SIZE,
          maxFileNameLength: MAX_FILENAME_LENGTH,
        },
      })

      try {
        const { data } = await createStandardOperatingProcedure({
          variables: {
            input: {
              name: sopTitle,
              externalId: fileId,
              facilityIds: facilityIds,
              type: values.role.value,
            },
          },
          refetchQueries: ['StandardOperatingProcedures'],
        })
        const sopReturnedTitle =
          data?.createStandardOperatingProcedure?.standardOperatingProcedure
            ?.name
        showSuccess(
          tVar(t, 'sops:crud_add_success', { name: sopReturnedTitle })
        )
        trackEvent('SOP Upload - Success', {
          sop_audience: values.role.value,
        })
        setIsLoading(false)
        onClose()
      } catch (error) {
        // Error creating SOP mutation
        trackEvent('SOP Upload - Failure', {
          sop_audience: values.role.value,
          error_type: 'mutation',
        })
        showError(error)
        setIsLoading(false)
      }
    } catch (error) {
      trackEvent('SOP Upload - Failure', {
        sop_audience: values.role.value,
        error_type: 'upload',
      })
      reportUploadError(error, UPLOAD_ERROR_COMPONENTS.CreateSOPModal)
      showError(error.message)
      setIsLoading(false)
    }
  }

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={!shouldEnableCreateSOP || !isValid}
      isSaveLoading={isLoading}
      maxW='600px'
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('sops:crud_upload_sop')}
      showCancelButton
      size='compact'
      title={t('sops:crud_add_sop')}
    >
      <Flex direction='column' gap='16px'>
        <ControlledTextInput
          colorScheme={colorScheme}
          control={control}
          data-testid='CreateSOPModal:title'
          disabled={isLoading}
          errors={errors}
          label={t('sops:crud_sop_title_label')}
          name='title'
          placeholder={t('sops:crud_sop_title_placeholder')}
          rules={{ required: t('sops:crud_sop_title_required') }}
          width='100%'
        />

        <Box>
          <FacilitySelectHeader
            allFacilitiesSelected={areAllFacilitiesSelected}
            colorScheme={colorScheme}
            dataTestId='CreateSOPModal:selectAllFacilities'
            isDisabled={isFacilitiesLoading || facilityOptions.length === 0}
            onToggleSelectAll={handleSelectAllFacilities}
          />
          <ControlledMultiSelectInput
            colorScheme={colorScheme}
            control={control}
            // The facility search is server-side and also matches by
            // address, so an address-only match's label may not contain the
            // typed query — 'internal' mode would re-filter it away.
            filterMode='external'
            name='facilities'
            onQueryChange={setFacilityName}
            options={facilityOptionsWithCue}
            placeholder={t('sops:crud_facilities_placeholder')}
            resultsHeader={t('sops:crud_facilities_label')}
            searchable
          />
        </Box>

        <ControlledSelect
          colorScheme={colorScheme}
          control={control}
          data-testid='CreateSOPModal:role'
          isDisabled={isLoading}
          label={t('sops:crud_available_to_label')}
          name='role'
          options={SOP_ROLES}
          placeholder={t('sops:crud_available_to_placeholder')}
          rules={{ required: true }}
        />

        <FormFileUploadControl
          accept='.pdf'
          colorScheme={colorScheme}
          errorMessage={errors.file && errors.file.message}
          id='file'
          inputRef={register({
            required: t('sops:crud_sop_pdf_required'),
            validate: (fl: FileList) =>
              fl?.[0]?.type === PDF_MIME_TYPE ||
              t('sops:crud_sop_pdf_format_error'),
          })}
          isDisabled={isLoading}
          isInvalid={!!errors.file}
          label={t('sops:crud_sop_pdf_label')}
          onFileRemoved={() => trigger(['file'])}
          placeholder={t('sops:crud_sop_pdf_placeholder')}
        />
      </Flex>
    </Modal>
  )
}
